import type { LphGroup } from './tenant-settings.js';

/**
 * Zod-freier Unterpfad `@asas/domain/lph-gruppen` für den Browser (AC-202-1).
 * Aus `work-package-library.ts` herausgelöst, weil die SPA die Funktion zur
 * Laufzeit braucht und jenes Modul Zod-Schemata auf Modulebene anlegt.
 * `work-package-library.ts` exportiert die Funktion weiter für die API.
 */

/**
 * Die Gruppe der Mandantenkonfiguration, der die Phasenmenge GENAU entspricht,
 * oder `null` (H2). Eine Teilmenge oder Obermenge einer Gruppe passt nicht.
 *
 * Für Service (Prüfung beim Schreiben) und Oberfläche (Gruppenanzeige,
 * Hinweis nach einer Umgruppierung) dieselbe Funktion.
 */
export function lphGruppeFuer(
  phasen: readonly number[],
  gruppen: readonly LphGroup[],
): LphGroup | null {
  const gesucht = new Set(phasen);

  return (
    gruppen.find(
      (gruppe) =>
        gruppe.phases.length === gesucht.size && gruppe.phases.every((phase) => gesucht.has(phase)),
    ) ?? null
  );
}

/**
 * Die HOAI-Leistungsphasen 1–9, zod-frei für den Browser. Dieselbe Grenze wie
 * `leistungsphaseSchema` in `common.ts`; `lph-gruppen.spec.ts` hält beide gleich.
 */
export const LEISTUNGSPHASEN = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/**
 * Die Phasen, die keiner Gruppe angehören, aufsteigend (AC-305 E6). Bei SBI
 * heute LPH 9. Die Ansicht zeigt sie als „ohne Gruppe“; Auswertungen führen sie
 * als eigene Position und lassen sie nie weg (AC-306).
 */
export function phasenOhneGruppe(gruppen: readonly LphGroup[]): number[] {
  const gruppiert = new Set(gruppen.flatMap((gruppe) => gruppe.phases));

  return LEISTUNGSPHASEN.filter((phase) => !gruppiert.has(phase));
}

/** Ergebnis von `lphGruppeMitPhasen`: eine Gruppe oder der Grund, warum keine. */
export type LphGruppenZuordnung =
  | { readonly art: 'eindeutig'; readonly gruppe: LphGroup }
  | { readonly art: 'mehrere_gruppen' }
  | { readonly art: 'ohne_gruppe' };

/**
 * Die EINE Gruppe der Mandantenkonfiguration, die ALLE Phasen enthält (AC-302
 * E4) — anders als `lphGruppeFuer` auch für eine Teilmenge: `{5}` und `{6, 7}`
 * gehören bei SBI beide zu „5-7“. Quelle ist allein `tenant_settings.lph_groups`;
 * eine eigene Übersetzungstabelle gibt es nicht (AC-305 E2).
 *
 * `ohne_gruppe`, sobald eine Phase keiner Gruppe angehört (z. B. 9), auch wenn
 * die übrigen eindeutig wären; `mehrere_gruppen`, wenn die Phasen auf mehr als
 * eine Gruppe fallen (z. B. 2 und 3, oder „1-4“). Eine leere Menge ist
 * `ohne_gruppe` — still zugeordnet wird nichts (AK 4).
 */
export function lphGruppeMitPhasen(
  phasen: readonly number[],
  gruppen: readonly LphGroup[],
): LphGruppenZuordnung {
  if (phasen.length === 0) return { art: 'ohne_gruppe' };

  const getroffen = new Set<LphGroup>();

  for (const phase of phasen) {
    const gruppe = gruppen.find((kandidat) => kandidat.phases.includes(phase));

    if (gruppe === undefined) return { art: 'ohne_gruppe' };
    getroffen.add(gruppe);
  }

  const [einzige, ...weitere] = getroffen;

  return einzige !== undefined && weitere.length === 0
    ? { art: 'eindeutig', gruppe: einzige }
    : { art: 'mehrere_gruppen' };
}
